import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import api from "../../utils/axios";
import { ColDef } from "ag-grid-enterprise";
import FilterSection from "../../components/FilterOrderSection";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { Button, Modal } from "antd";
import useLang from "../../hooks/useLang";
import { toast } from "react-toastify";
import PaginationTable from "../../components/Pagination";
import "./index.css";
import { currencySymbol, valueDateFormatter } from "../../utils/formatters";
import { exportDataToCsv } from "../../utils/exportDataToCsv";
import { useMediaQuery } from "@mui/material";
import { CONFIG } from "../../config";

const buyerValueGetter = (params: any) => {
	const text =
		params.data.buyerNameAndSurname +
		// "\n" +
		" (" +
		params.data.buyerPhoneNo +
		")" +
		"\n" +
		params.data.city +
		", " +
		params.data.country;
	return text;
};
const codeFormatter = (params: any) => {
	const date = valueDateFormatter(params.data.createdAt);
	const text =
		"#" +
		params.data.displayId +
		" (" +
		params.data.statusDescription +
		") " +
		"\n " +
		date;
	return text;
};

const RegisteredOrders = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);

	const grid = useRef<AgGridReact | null>(null);
	const [tableData, setTableData] = useState([]);
	const [columnId, setColumnId] = useState();
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [open, setOpen] = useState<boolean>(false);
	const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
	const [isWaitingForPickupModalOpen, setIsWaitingForPickupModalOpen] =
		useState<boolean>(false);
	const [waitingPickupOrderList, setWaitingPickupOrderList] = useState<any>(
		[]
	);
	const navigate = useNavigate();
	const { strings } = useLang("registerOrder");

	const calculateRowHeight = (params: any) => {
		const cellContent =
			params.data.buyerNameAndSurname +
			"\n" +
			"(" +
			params.data.buyerPhoneNo +
			")" +
			"\n" +
			params.data.createdAt;

		const lineHeight = 30; // Adjust this value as needed
		const numLines = cellContent.split("\n").length;
		const calculatedHeight = lineHeight * numLines;

		return calculatedHeight;
	};

	const orderDecriptionValueGetter = (params: any) => {
		// Symbol comes from the order's own registration currency, not the deployment's.
		const symbol = currencySymbol(params.data.orderCurrencyId);
		const text =
			`${strings?.totalPrice}` +
			params.data.orderPrice +
			symbol +
			"\n" +
			`${strings?.transport}` +
			params.data.transportPrice +
			symbol;

		return text;
	};

	const getTableData = useCallback(async () => {
		try {
			const response = await api.get("Order/registered-orders");
			if (response.data) {
				const result = response.data;
				setTableData(result);
				setFilteredOrders(result);
			}
		} catch (error: any) {
			console.log(error);
		}
	}, []);

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		getTableData();
		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 170,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.code,
				valueGetter: codeFormatter,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
					"white-space": "pre",
				}),
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "buyerNameAndSurname",
				headerName: strings?.buyer,
				valueGetter: buyerValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescription,
				valueGetter: orderDecriptionValueGetter,
				cellStyle: { "white-space": "pre" },
				width: 350,
				suppressSizeToFit: isSmallScreen,
			},
		]);
	}, [getTableData, setTableColumns, strings]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "button":
				await onButtonTouch(params?.data);
				break;
			case "id":
				await onOrderIdClicked(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const onButtonTouch = (data: any) => {
		setOpen(true);
		setColumnId(data.id);
	};

	const confirmWaitingPickup = async () => {
		const orderId = [{ orderId: columnId }];
		try {
			const response = await api.post(
				"Order/clientorderstatusupdate",
				orderId
			);
			if (response.status === 200) {
				setOpen(false);
				await getTableData();
				toast.success("Porosia u vendos per grumbullim");
			}
		} catch (error: any) {
			console.log(error);
		}
	};

	const filterOrders = (
		countryId: any,
		cityId: any,
		statusIds: any[],
		id: any,
		orderDescription: any,
		fromDate: any,
		toDate: any,
		filterType: any
	) => {
		const isDateFilter = [
			"registration_date",
			"acceptance_date",
			"closure_date",
		].includes(filterType);
		const data = tableData.filter((item: any) => {
			if (
				(countryId != null && item.countryId !== countryId) ||
				(cityId != null && item.cityId !== cityId) ||
				(statusIds?.length > 0 &&
					!statusIds?.includes(item.statusId)) ||
				(id?.toString()?.trim()?.length && item.id.toString() !== id) ||
				(orderDescription?.toString()?.trim()?.length &&
					!JSON.stringify(item)
						?.toLowerCase()
						?.includes(orderDescription?.toLowerCase()?.trim()))
			) {
				return false;
			}

			if (isDateFilter && fromDate && toDate) {
				const itemDate = new Date(
					filterType === "registration_date"
						? item.createdAt
						: item.pickedUpDate
				);
				const fromDateObj = new Date(fromDate);
				const toDateObj = new Date(toDate);

				if (itemDate < fromDateObj || itemDate > toDateObj) {
					return false;
				}
			}

			return true;
		});
		setFilteredOrders(data);
		setCurrentPage(1);
	};

	const customOptions = [
		{
			label: strings?.customOptions.printTicket,
			value: "print_ticket",
		},
		{
			label: strings?.customOptions.printList,
			value: "print_list",
		},
		{
			label: strings?.customOptions.exportExcel,
			value: "export_excel",
		},
		{
			label: strings?.customOptions.readyForCollection,
			value: "ready",
		},
		{
			label: strings?.customOptions.delete,
			value: "delete",
		},
	];

	const handleClientStatusChangeToWaitingPickup = async () => {
		if (waitingPickupOrderList && waitingPickupOrderList.length > 0) {
			try {
				const response = await api.post(
					"Order/clientorderstatusupdate",
					waitingPickupOrderList
				);

				if (response.status === 200) {
					toast.success("Statusi u ndryshua me sukses!");
					setWaitingPickupOrderList([]);
					getTableData();
					setIsWaitingForPickupModalOpen(false);
				} else {
					toast.error("Ndryshimi i statusit deshtoj!");
				}
			} catch (error) {
				console.error(
					"An error occurred while making the API request:",
					error
				);
				toast.error(
					"Një gabim ndodhi. Ju lutemi provoni përsëri më vonë"
				);
			}
		}
	};

	const orderStatusChangeToWatingForPickup = async () => {
		const selectedRows = grid.current?.api?.getSelectedRows();
		if (selectedRows && selectedRows.length > 0) {
			const checkStatusId = selectedRows.some(
				(item: any) => item.statusId !== 1
			);
			if (checkStatusId) {
				toast.error(
					"Ju lutem filtroni dhe selektoni vetem porosite te cilat kane statusin Regjistruara"
				);
				return;
			} else {
				const orderList = selectedRows.map((order) => ({
					orderId: order.id,
				}));
				setWaitingPickupOrderList(orderList);
				setIsWaitingForPickupModalOpen(true);
			}
		} else {
			toast.error(
				"Ju lutem selektoni porosite qe deshironi te i vendosni ne Gati per Grumbullim"
			);
		}
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const onPageChanged = (page: any) => {
		setCurrentPage(page);
	};
	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};
	const paginatedOrders = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return filteredOrders.slice(startIndex, endIndex);
	}, [currentPage, filteredOrders]);

	const totalOrders = filteredOrders.length;
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, totalOrders - startOrderIndex);

	const selectedColumnIds = ["id", "buyerNameAndSurname", "orderDescription"];

	const onExportSelectedId = () => {
		const dataToExport = filteredOrders.map((item) => {
			const date = valueDateFormatter(item.createdAt);
			const id =
				"#" +
				item.displayId +
				" (" +
				item.statusDescription +
				") " +
				"\n " +
				date;
			return {
				id,
				buyerNameAndSurname:
					item.buyerNameAndSurname +
					// "\n" +
					" (" +
					item.buyerPhoneNo +
					")" +
					"\n" +
					item.city +
					", " +
					item.country,
				orderDescription:
					`${strings?.totalPrice}` +
					item.orderPrice +
					currencySymbol(item.orderCurrencyId) +
					"\n" +
					`${strings?.transport}` +
					item.transportPrice +
					currencySymbol(item.orderCurrencyId),
			};
		});
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const printTickets = async (format: string) => {
		const ids = grid?.current?.api?.getSelectedRows()?.map((item) => ({
			id: item.displayId,
		}));

		if (!ids?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		// const formatA4 = format === "A4";
		const params =
			format === "A4"
				? { formatA4: true }
				: format === "harleyTicket"
				? { harleyTicket: true }
				: {};
		try {
			const response = await api.post("Order/print-order-ticket", ids, {
				params,
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const printOrderList = async () => {
		const ids = grid?.current?.api.getSelectedRows()?.map((item: any) => {
			return { id: item.id };
		});

		if (!ids || ids.length === 0) {
			toast.warning("No orders selected for printing");
			return;
		}

		try {
			const response = await api.post("Order/order-list-details", ids, {
				responseType: "blob",
			});

			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup is blocked. Please allow pop-ups for this page."
				);
			}
		} catch (error) {
			console.error("Error exporting order list details:", error);
		}
	};

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<FilterSection
						title={strings?.registerordertitle}
						onFilterChange={filterOrders}
						selectedOptions={customOptions}
						onSelectedOrderStatusChange={
							orderStatusChangeToWatingForPickup
						}
						onExportSelectedId={onExportSelectedId}
						showAdminFilters={false}
						onPrintTickets={printTickets}
						onPrintList={printOrderList}
					/>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={filteredOrders.length}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							height: "auto",
							width: "100%",
						}}
					>
						<AgGridReact
							rowData={paginatedOrders}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							getRowHeight={calculateRowHeight}
							onCellClicked={onCellClicked}
							domLayout="autoHeight"
							paginationPageSize={pageSize}
							rowHeight={60}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={open}
				onOk={() => setOpen(false)}
				onCancel={() => setOpen(false)}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-center justify-center">
							<Button
								type="primary"
								size="large"
								// className="bg-blue-600"
								style={{ backgroundColor: "#4F16C2" }}
								onClick={confirmWaitingPickup}
							>
								{strings?.confirmbutton}
							</Button>
							<CancelBtn />
						</div>
					</>
				)}
			>
				<div className="flex flex-col gap-4 items-center p-10">
					<QuestionCircleOutlined style={{ fontSize: "60px" }} />
					<p style={{ fontSize: 17 }}>
						{strings?.confirmation} #{columnId}
						<br /> {strings?.confirmation2}
					</p>
				</div>
			</Modal>
			<Modal
				title={
					"A jeni te sigurte qe deshironi te ndryshoni statusin  ne  Gati per grumbullim?"
				}
				open={isWaitingForPickupModalOpen}
				onOk={handleClientStatusChangeToWaitingPickup}
				onCancel={() => setIsWaitingForPickupModalOpen(false)}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				{/* <p>
						Ju lutem komfirmoni se porosia juaj është gati për
						grumbulli dhe mund te mbledhet nga posta.
					</p> */}
			</Modal>
		</>
	);
};

export default RegisteredOrders;
